import React, { useState, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SaveIcon from '@material-ui/icons/Save';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CircularProgress from '@material-ui/core/CircularProgress';
import { fetchCMOMembers } from '../../../containers/actions/common/inventoryStatus';
import { fetchUserfromDepartment } from '../../../../../containers/actions/userActions';
import { fetchOrgDeptRoles, fetchCmoDeptRoles, fetchRolesByEvent } from '../../../containers/actions/common/notificationsAction';

const ORANGE = '#e36e39';

// ─── styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 8,
    maxHeight: '82vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  // ── fixed header (cancel row + title + restrict by) ────────────────────────
  fixedTop: {
    padding: '0.75rem 1.5rem 0.5rem',
    flexShrink: 0,
  },
  cancelRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.25rem',
  },
  cancelBtn: {
    textTransform: 'none',
    fontSize: '0.875rem',
    color: '#333',
    fontWeight: 500,
    padding: '2px 0',
    minWidth: 0,
    '&:hover': { backgroundColor: 'transparent' },
  },
  cancelIcon: {
    color: '#e53935',
    fontSize: '1.2rem',
  },
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '1.5rem',
    color: '#111',
    marginBottom: '0.75rem',
  },
  restrictByRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '0.5rem',
  },
  restrictByLabel: {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: '#111',
    marginRight: '0.25rem',
  },
  restrictCheckLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.8125rem',
    },
  },

  // ── scrollable body ────────────────────────────────────────────────────────
  scrollBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 1.5rem 0.5rem',
  },

  // ── event info table ───────────────────────────────────────────────────────
  eventTableWrap: {
    border: '1px solid #c5d8ec',
    borderRadius: 2,
    overflowX: 'auto',
    marginBottom: '1rem',
  },
  eventTableHead: {
    backgroundColor: '#D9E8F5',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      fontSize: '0.8125rem',
      color: '#333',
      padding: '6px 12px',
      borderBottom: 'none',
    },
  },
  eventTableBody: {
    '& .MuiTableCell-body': {
      fontSize: '0.8125rem',
      color: '#333',
      padding: '6px 12px',
    },
  },

  // ── roles table ────────────────────────────────────────────────────────────
  rolesTableWrap: {
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    overflowX: 'auto',
    marginBottom: '0.75rem',
  },
  rolesTableHead: {
    backgroundColor: '#f5f5f5',
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.8125rem',
      color: '#222',
      padding: '7px 10px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'nowrap',
    },
  },
  rolesCell: {
    fontSize: '0.8125rem',
    color: '#333',
    padding: '6px 10px',
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  deleteIcon: {
    color: '#e53935',
    cursor: 'pointer',
    fontSize: '1.15rem',
    '&:hover': { color: '#b71c1c' },
  },
  saveRowIcon: {
    color: '#555',
    cursor: 'pointer',
    fontSize: '1.15rem',
    '&:hover': { color: '#111' },
  },
  cancelNewRowIcon: {
    color: '#e53935',
    cursor: 'pointer',
    fontSize: '1.15rem',
    marginLeft: '0.35rem',
    '&:hover': { color: '#b71c1c' },
  },
  newRowActions: {
    display: 'flex',
    alignItems: 'center',
  },

  // ── inline new-row selects ─────────────────────────────────────────────────
  inlineSelect: {
    fontSize: '0.8125rem',
    padding: '4px 6px',
    border: '1px solid #bbb',
    borderRadius: 4,
    backgroundColor: '#fff',
    cursor: 'pointer',
    width: '100%',
    maxWidth: 150,
  },

  // ── add new role button ────────────────────────────────────────────────────
  addRoleBtn: {
    textTransform: 'none',
    fontSize: '0.8125rem',
    color: '#333',
    border: '1px solid #bbb',
    borderRadius: '2rem',
    padding: '4px 14px',
    marginBottom: '0.5rem',
    '&:hover': { borderColor: '#555', backgroundColor: 'transparent' },
  },

  // ── fixed footer ───────────────────────────────────────────────────────────
  fixedBottom: {
    padding: '0.75rem 1.5rem 1.25rem',
    textAlign: 'center',
    flexShrink: 0,
    borderTop: '1px solid #f0f0f0',
  },
  saveChangesBtn: {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 600,
    backgroundColor: ORANGE,
    color: '#fff',
    borderRadius: '2rem',
    padding: '7px 28px',
    '&:hover': { backgroundColor: '#c95c2b' },
    '&.Mui-disabled': { backgroundColor: '#ccc', color: '#888' },
  },
  switchBase: {
    '&.Mui-checked': {
      color: '#007bff',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#007bff',
    },
  },
}));

// Acronyms that must always render in full caps in event names / labels.
const EVENT_NAME_ACRONYMS = new Set([
  'BMS', 'CMO', 'ABO', 'PO', 'EU', 'ASN', 'SAP', 'API',
  'GRN', 'PCMP', 'ERP', 'UOM', 'QC', 'QA', 'CC', 'SL', 'ID',
]);

const formatEventName = (name) => {
  if (!name) return '';
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // "CMOUser" -> "CMO User"
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')        // "batchAllocation" -> "batch Allocation"
    .replace(/[_-]+/g, ' ')                          // snake_case / kebab -> spaces
    .trim()
    .split(/\s+/)
    .map((w) => {
      const upper = w.toUpperCase();
      if (EVENT_NAME_ACRONYMS.has(upper)) return upper;            // BMS, CMO, PO, EU, ASN...
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); // Title Case each word
    })
    .join(' ');
};

// ─── component ────────────────────────────────────────────────────────────────

const EventEditViewDialog = ({ open, onClose, event, mode, onSave, saving }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [roles, setRoles] = useState([]);
  const [deletedRoles, setDeletedRoles] = useState([]);
  const [restrictBy, setRestrictBy] = useState({ brand: true, cmo: true, productType: true });
  const [newRow, setNewRow] = useState(null);
  const initialSnapshot = useRef({ roles: [], restrictBy: {} });

  const isEdit = mode === 'edit';

  // ── Org / Dept / Role dropdown data from Redux ──────────────────────────────
  const cmosDetails = useSelector((state) => state?.pcmp?.common?.inventoryStatus?.cmosDetails);
  const orgDeptRoles = useSelector((state) => state?.pcmp?.common?.notifications?.orgDeptRoles);
  const cmoDeptRoles = useSelector((state) => state?.pcmp?.common?.notifications?.cmoDeptRoles);
  const deptLoading = useSelector((state) => state?.user?.fetchUserfromDeptLoading);
  const departmentDetailsList = useSelector(
    (state) => state?.user?.fetchUserfromDeptDetails?.departmentDetailsList || []
  );
  const eventRoles = useSelector((state) => state?.pcmp?.common?.notifications?.eventRoles || []);
  const eventRolesLoading = useSelector((state) => state?.pcmp?.common?.notifications?.eventRolesLoading);
  const orgLoading = !orgDeptRoles && isEdit;

  // Organisation options — prefer orgDeptRoles (has both BMS + CMO), fall back to cmosDetails
  const orgOptionsList = useMemo(() => {
    if (orgDeptRoles?.organizations) return orgDeptRoles.organizations;
    const members = cmosDetails?.CMOMembersWithId || [];
    return members.map((m) => ({ organization_name: m.organizationName, organization_id: m.organization_id, orgType: 'CMO' }));
  }, [orgDeptRoles, cmosDetails]);

  const orgOptions = useMemo(() => orgOptionsList.map((o) => o.organization_name).filter(Boolean), [orgOptionsList]);

  // Detect whether the selected org in the new role row is BMS or CMO
  const selectedOrgType = useMemo(() => {
    if (!newRow?.organization) return null;
    const org = orgOptionsList.find((o) => o.organization_name === newRow.organization);
    return org?.orgType || null;
  }, [newRow?.organization, orgOptionsList]);

  // Department options:
  // BMS → from departmentDetailsList (BMS Corda node)
  // CMO → from cmoDeptRoles.departments (CMO Corda node via fetchCmoDeptRoles)
  const deptOptions = useMemo(() => {
    if (selectedOrgType === 'CMO' && cmoDeptRoles?.departments?.length) {
      return cmoDeptRoles.departments.map((d) => d.departmentName).filter(Boolean);
    }
    return departmentDetailsList.map((d) => d.departmentName).filter(Boolean);
  }, [departmentDetailsList, selectedOrgType, cmoDeptRoles]);

  // Role options — derived from the selected department's rolesList
  // BMS → from departmentDetailsList (BMS Corda node)
  // CMO → from cmoDeptRoles.departments (CMO Corda node)
  const roleOptions = useMemo(() => {
    if (!newRow?.department) return [];
    if (selectedOrgType === 'CMO' && cmoDeptRoles?.departments?.length) {
      const dept = cmoDeptRoles.departments.find((d) => d.departmentName === newRow.department);
      return (dept?.rolesList || []).map((r) => r.role_name).filter(Boolean);
    }
    const dept = departmentDetailsList.find((d) => d.departmentName === newRow.department);
    return (dept?.rolesList || []).map((r) => r.role_name).filter(Boolean);
  }, [departmentDetailsList, newRow?.department, selectedOrgType, cmoDeptRoles]);

  // Fetch org/dept/role lookup data when dialog opens (both view and edit need name resolution)
  useEffect(() => {
    if (open) {
      if (!orgDeptRoles) dispatch(fetchOrgDeptRoles());
      if (!cmosDetails) dispatch(fetchCMOMembers());
      if (!departmentDetailsList.length) dispatch(fetchUserfromDepartment());
      // Fetch roles for this specific event from Corda (cross-node enriched)
      if (event?.eventTypeID) dispatch(fetchRolesByEvent(event.eventTypeID));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ID → name resolver helpers ──────────────────────────────────────────────
  const resolveRoleNames = (collaboratorRoleID, departmentID, vendorMosymphonyID, collaboratorRoleName, departmentNameFromApi) => {
    let organization = '';
    let department = '';
    let role = '';

    // Resolve department & role from departmentDetailsList
    if (departmentDetailsList.length) {
      // First try matching by collaboratorRoleID
      for (const dept of departmentDetailsList) {
        const matchedRole = (dept.rolesList || []).find(
          (r) => r.collaborator_role_id === collaboratorRoleID
        );
        if (matchedRole) {
          department = dept.departmentName || '';
          role = matchedRole.role_name || '';
          break;
        }
      }

      // If no match by ID, try matching by collaboratorRoleName
      if (!role && collaboratorRoleName) {
        const trimmedName = collaboratorRoleName.trim().toLowerCase();
        // Also try without "BMS " or "CMO " prefix
        const strippedName = trimmedName.replace(/^(bms|cmo)\s+/i, '');
        for (const dept of departmentDetailsList) {
          const matchedRole = (dept.rolesList || []).find((r) => {
            if (!r.role_name) return false;
            const rName = r.role_name.trim().toLowerCase();
            return rName === trimmedName || rName === strippedName;
          });
          if (matchedRole) {
            department = dept.departmentName || '';
            role = matchedRole.role_name || '';
            break;
          }
        }
      }

      // Fall back to departmentID match if still no department
      if (!department && departmentID) {
        const dept = departmentDetailsList.find((d) => d.departmentID === departmentID);
        if (dept) department = dept.departmentName || '';
      }
    }

    // Fall back to departmentName from API response (enriched from CMO cross-node query)
    if (!department && departmentNameFromApi) {
      department = departmentNameFromApi;
    }

    // If role still not resolved (CMO roles not in BMS departmentDetailsList),
    // try orgDeptRoles.roles from SML
    if (!role && orgDeptRoles?.roles?.length) {
      const smlMatch = orgDeptRoles.roles.find(
        (r) => r.collaborator_role_id === collaboratorRoleID
      );
      if (smlMatch) {
        role = smlMatch.role_name || '';
      } else if (collaboratorRoleName) {
        // Try by name match
        const trimmedName = collaboratorRoleName.trim().toLowerCase();
        const smlNameMatch = orgDeptRoles.roles.find(
          (r) => r.role_name && r.role_name.trim().toLowerCase() === trimmedName
        );
        if (smlNameMatch) role = smlNameMatch.role_name || '';
      }
    }

    // Final fallback: use collaboratorRoleName directly from the Corda response
    if (!role && collaboratorRoleName) {
      role = collaboratorRoleName.trim();
    }

    // Resolve organization — try orgDeptRoles first (has BMS + CMO), fall back to cmosDetails
    if (vendorMosymphonyID) {
      const orgMatch = orgOptionsList.find((o) => o.organization_id === vendorMosymphonyID);
      if (orgMatch) {
        organization = orgMatch.organization_name || '';
      } else if (cmosDetails?.CMOMembersWithId) {
        const member = cmosDetails.CMOMembersWithId.find(
          (m) => m.organization_id === vendorMosymphonyID || m.vendorMosymphonyID === vendorMosymphonyID
        );
        if (member) organization = member.organizationName || '';
      }
    }

    return { organization, department, role };
  };

  // Seed roles from fetchRolesByEvent response (Corda enriched — has role names + dept names)
  useEffect(() => {
    if (open && event && !eventRolesLoading && eventRoles.length >= 0) {
      // Use eventRoles from Redux (fetched via fetchRolesByEvent) — Corda returns names directly
      const rolesToUse = eventRoles.length > 0 ? eventRoles : (event.roles || []);

      // Resolve organization name from vendorMosymphonyID using org list
      const resolveOrgName = (vendorMosymphonyID) => {
        if (!vendorMosymphonyID) return '';
        const orgMatch = orgOptionsList.find((o) => o.organization_id === vendorMosymphonyID);
        if (orgMatch) return orgMatch.organization_name || '';
        if (cmosDetails?.CMOMembersWithId) {
          const member = cmosDetails.CMOMembersWithId.find(
            (m) => m.organization_id === vendorMosymphonyID || m.vendorMosymphonyID === vendorMosymphonyID
          );
          if (member) return member.organizationName || '';
        }
        return '';
      };

      const apiRoles = rolesToUse.map((r, idx) => {
        const orgID = r.vendorMosymphonyID || r.orgID || null;
        return {
          id: r.eventToRoleMappingID || r.collaboratorRoleID || r.id || idx + 1,
          eventToRoleMappingID: r.eventToRoleMappingID || null,
          collaboratorRoleID: r.collaboratorRoleID || null,
          departmentID: r.departmentID || null,
          vendorMosymphonyID: orgID,
          organizationID: orgID,
          organization: resolveOrgName(orgID),
          department: r.departmentName || '',
          role: r.collaboratorRoleName || '',
          actionRequired: r.actionRequired || false,
        };
      });
      setRoles(apiRoles);
      // Initialise restrict-by from event-level flags or first role's flags
      const firstRole = rolesToUse[0] || {};
      const initRestrict = {
        brand: event.brandUserAssociationReqd || firstRole.brandUserAssociationReqd || false,
        cmo: event.cmoUserAssociationReqd || firstRole.cmoUserAssociationReqd || false,
        productType: event.productTypeUserAssociationReqd || firstRole.productTypeUserAssociationReqd || false,
      };
      setRestrictBy(initRestrict);
      initialSnapshot.current = {
        roles: apiRoles.map((r) => r.id),
        restrictBy: initRestrict,
        actionRequired: apiRoles.reduce((acc, r) => ({ ...acc, [r.id]: r.actionRequired }), {}),
      };
      setDeletedRoles([]);
      setNewRow(null);
    }
  }, [open, event, eventRoles, eventRolesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (id) => {
    setRoles((prev) => prev.map((r) => r.id === id ? { ...r, actionRequired: !r.actionRequired } : r));
  };

  const handleDelete = (id) => {
    const role = roles.find((r) => r.id === id);
    if (role?.eventToRoleMappingID) {
      setDeletedRoles((prev) => [...prev, role]);
    }
    setRoles((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAddNewRole = () => {
    setNewRow({ organization: '', department: '', role: '', collaboratorRoleID: null, departmentID: null, organizationID: null, actionRequired: false });
  };

  const handleSaveNewRow = () => {
    const canSave = newRow.organization && newRow.department && newRow.role;
    if (canSave) {
      setRoles((prev) => [...prev, {
        id: Date.now(),
        organization: newRow.organization,
        department: newRow.department,
        role: newRow.role,
        collaboratorRoleID: newRow.collaboratorRoleID,
        departmentID: newRow.departmentID,
        organizationID: newRow.organizationID,
        actionRequired: newRow.actionRequired,
      }]);
      setNewRow(null);
    }
  };

  // Dirty check — enable Save Changes only when something changed
  const isDirty = useMemo(() => {
    const snap = initialSnapshot.current;
    const currentIds = roles.map((r) => r.id);
    // roles added or removed or deleted
    if (deletedRoles.length > 0) return true;
    if (currentIds.length !== snap.roles.length || !currentIds.every((id, i) => id === snap.roles[i])) return true;
    // restrict-by changed
    if (restrictBy.brand !== snap.restrictBy.brand || restrictBy.cmo !== snap.restrictBy.cmo || restrictBy.productType !== snap.restrictBy.productType) return true;
    // action required toggled
    if (roles.some((r) => r.actionRequired !== snap.actionRequired[r.id])) return true;
    return false;
  }, [roles, restrictBy, deletedRoles]);

  const handleSaveChanges = () => {
    if (onSave && event) {
      // All current roles (existing + newly added) + deleted ones marked
      const allRoles = roles.map((r) => ({
        ...(r.eventToRoleMappingID ? { eventToRoleMappingID: r.eventToRoleMappingID } : {}),
        collaboratorRoleID: r.collaboratorRoleID,
        departmentID: r.departmentID || null,
        orgID: r.organizationID || r.vendorMosymphonyID || null,
        actionRequired: r.actionRequired,
        markForDelete: false,
      }));

      // Append deleted roles
      deletedRoles.forEach((r) => {
        allRoles.push({
          eventToRoleMappingID: r.eventToRoleMappingID,
          collaboratorRoleID: r.collaboratorRoleID,
          departmentID: r.departmentID || null,
          orgID: r.organizationID || r.vendorMosymphonyID || null,
          actionRequired: r.actionRequired,
          markForDelete: true,
        });
      });

      onSave({
        eventTypeID: event.eventTypeID,
        brandUserAssociationReqd: restrictBy.brand,
        CMOUserAssociationReqd: restrictBy.cmo,
        productTypeUserAssociationReqd: restrictBy.productType,
        roles: allRoles,
      });
    } else {
      onClose();
    }
  };

  if (!event) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth='md'
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
    >
      {/* ── fixed top ─────────────────────────────────────────────────────── */}
      <Box className={classes.fixedTop}>
        <div className={classes.cancelRow}>
          <Button
            className={classes.cancelBtn}
            disableRipple
            endIcon={<CloseIcon className={classes.cancelIcon} />}
            onClick={saving ? undefined : onClose}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>

        <Typography className={classes.dialogTitle}>Manage Distribution List</Typography>

        <div className={classes.restrictByRow}>
          <Typography component="span" className={classes.restrictByLabel}>Restrict by:</Typography>
          {[
            { key: 'brand',       label: 'Brand' },
            { key: 'cmo',         label: 'CMO' },
            { key: 'productType', label: 'Product Type' },
          ].map(({ key, label }) => (
            <FormControlLabel
              key={key}
              className={classes.restrictCheckLabel}
              style={{ marginRight: 6 }}
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={restrictBy[key]}
                  onChange={() => isEdit && setRestrictBy((p) => ({ ...p, [key]: !p[key] }))}
                  style={!isEdit ? { pointerEvents: 'none' } : undefined}
                />
              }
              label={label}
            />
          ))}
        </div>
      </Box>

      {/* ── scrollable body ───────────────────────────────────────────────── */}
      <Box className={classes.scrollBody}>

        {/* event info */}
        {/* roles table */}
        <div className={classes.rolesTableWrap}>
          {eventRolesLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
              <CircularProgress size={28} style={{ color: '#e36e39' }} />
              <span style={{ marginLeft: 12, fontSize: '0.875rem', color: '#666' }}>Loading roles...</span>
            </div>
          ) : (
          <Table size="small">
            <TableHead className={classes.rolesTableHead}>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Action Required / Information Only</TableCell>
                {isEdit && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.filter((r) => r.organization || r.department || r.role).length === 0 && !isEdit ? (
                <TableRow>
                  <TableCell
                    colSpan={isEdit ? 5 : 4}
                    align="center"
                    className={classes.rolesCell}
                    style={{ padding: '24px 10px', color: '#888' }}
                  >
                    No roles have been assigned to this event yet.
                  </TableCell>
                </TableRow>
              ) : (
                roles
                  .filter((r) => r.organization || r.department || r.role)
                  .map((r) => (
                    <TableRow key={r.id}>
                      <Tooltip title={r.organization || ''} arrow disableHoverListener={!r.organization}>
                        <TableCell className={classes.rolesCell}>{r.organization}</TableCell>
                      </Tooltip>
                      <Tooltip title={r.department || ''} arrow disableHoverListener={!r.department}>
                        <TableCell className={classes.rolesCell}>{r.department}</TableCell>
                      </Tooltip>
                      <Tooltip title={r.role || ''} arrow disableHoverListener={!r.role}>
                        <TableCell className={classes.rolesCell}>{r.role}</TableCell>
                      </Tooltip>
                      <TableCell className={classes.rolesCell}>
                        <Switch
                          size="small"
                          checked={!!r.actionRequired}
                          onChange={() => isEdit && handleToggle(r.id)}
                          classes={{ switchBase: classes.switchBase }}
                          style={!isEdit ? { pointerEvents: 'none' } : undefined}
                        />
                      </TableCell>
                      {isEdit && (
                        <TableCell className={classes.rolesCell} align="center" style={{ overflow: 'visible', textAlign: 'center' }}>
                          <DeleteOutlineIcon
                            className={classes.deleteIcon}
                            onClick={() => handleDelete(r.id)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
              )}

              {/* inline new-row entry */}
              {isEdit && newRow && (
                <TableRow>
                  <TableCell className={classes.rolesCell}>
                    <select
                      className={classes.inlineSelect}
                      value={newRow.organization}
                      onChange={(e) => {
                        const orgName = e.target.value;
                        const org = orgOptionsList.find((o) => o.organization_name === orgName);
                        setNewRow((p) => ({ ...p, organization: orgName, organizationID: org?.organization_id || null, department: '', role: '', collaboratorRoleID: null, departmentID: null }));
                        // CMO: fetch departments + roles from CMO Corda node via legal name
                        // BMS: already loaded from local Corda node
                        if (org?.orgType === 'CMO' && org?.organization_id) {
                          dispatch(fetchCmoDeptRoles(org.organization_id));
                        }
                      }}
                      disabled={orgLoading}
                    >
                      <option value="">{orgLoading ? 'Loading...' : 'Select'}</option>
                      {orgOptionsList.map((o) => (
                        <option key={o.organization_name} value={o.organization_name}>
                          {o.organization_name}{o.orgType ? ` (${o.orgType})` : ''}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell className={classes.rolesCell}>
                    <select
                      className={classes.inlineSelect}
                      value={newRow.department}
                      onChange={(e) => {
                        const deptName = e.target.value;
                        // Resolve departmentID from the correct source
                        const deptList = (selectedOrgType === 'CMO' && cmoDeptRoles?.departments?.length)
                          ? cmoDeptRoles.departments
                          : departmentDetailsList;
                        const dept = deptList.find((d) => d.departmentName === deptName);
                        setNewRow((p) => ({ ...p, department: deptName, departmentID: dept?.departmentID || null, role: '', collaboratorRoleID: null }));
                      }}
                      disabled={deptLoading || (selectedOrgType === 'CMO' && !cmoDeptRoles)}
                    >
                      <option value="">{(selectedOrgType === 'CMO' && !cmoDeptRoles) ? 'Loading...' : 'Select'}</option>
                      {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className={classes.rolesCell}>
                    <select
                      className={classes.inlineSelect}
                      value={newRow.role}
                      onChange={(e) => {
                        const roleName = e.target.value;
                        let roleObj = null;
                        if (selectedOrgType === 'CMO' && cmoDeptRoles?.departments?.length) {
                          // CMO: resolve from cmoDeptRoles.departments (from CMO Corda node)
                          const dept = cmoDeptRoles.departments.find((d) => d.departmentName === newRow.department);
                          roleObj = (dept?.rolesList || []).find((r) => r.role_name === roleName);
                        } else {
                          // BMS: resolve from departmentDetailsList (local Corda)
                          const dept = departmentDetailsList.find((d) => d.departmentName === newRow.department);
                          roleObj = (dept?.rolesList || []).find((r) => r.role_name === roleName);
                        }
                        setNewRow((p) => ({ ...p, role: roleName, collaboratorRoleID: roleObj?.collaborator_role_id || null }));
                      }}
                      disabled={!newRow.department}
                    >
                      <option value="">Select</option>
                      {roleOptions.map((ro) => <option key={ro} value={ro}>{ro}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className={classes.rolesCell}>
                    <Switch
                      size="small"
                      checked={!!newRow.actionRequired}
                      onChange={() => setNewRow((p) => ({ ...p, actionRequired: !p.actionRequired }))}
                      classes={{ switchBase: classes.switchBase }}
                    />
                  </TableCell>
                  <TableCell className={classes.rolesCell} align="center" style={{ overflow: 'visible', textAlign: 'center' }}>
                    <div className={classes.newRowActions} style={{ justifyContent: 'center' }}>
                      <Tooltip title="Save" arrow>
                        <span>
                          <SaveIcon
                            className={classes.saveRowIcon}
                            onClick={newRow.organization && newRow.department && newRow.role ? handleSaveNewRow : undefined}
                            style={!(newRow.organization && newRow.department && newRow.role) ? { color: '#ccc', cursor: 'default' } : undefined}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel" arrow><CloseIcon className={classes.cancelNewRowIcon} onClick={() => setNewRow(null)} /></Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </div>

        {/* add new role */}
        {isEdit && (
          <Button
            className={classes.addRoleBtn}
            variant="outlined"
            disableElevation
            startIcon={<AddCircleOutlineIcon style={{ fontSize: '1rem' }} />}
            onClick={handleAddNewRole}
            disabled={!!newRow}
          >
            Add New Role
          </Button>
        )}
      </Box>

      {/* ── fixed footer (edit mode only) ─────────────────────────────────── */}
      {isEdit && (
        <Box className={classes.fixedBottom}>
          <Button
            className={classes.saveChangesBtn}
            variant="contained"
            disableElevation
            endIcon={saving ? null : <ChevronRightIcon />}
            onClick={handleSaveChanges}
            disabled={!isDirty || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
            {saving && <CircularProgress size={16} style={{ color: '#fff', marginLeft: 8 }} />}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default EventEditViewDialog;
